import React, { useEffect, useState } from 'react';
import aiLevels from '../levels-ai';

/**
 * GoldenAIDashboard - Main dashboard for Golden AI (Learning AI)
 *
 * Shows 12 AI literacy levels with progress tracking
 * Entry requirement: Level 10+ of Golden Learn completed
 */
function GoldenAIDashboard({ onBackToSIFISO, onSelectLevel }) {
  const [completedAILevels, setCompletedAILevels] = useState([]);
  const [goldenLearnProgress, setGoldenLearnProgress] = useState(0);
  const [meetsRequirement, setMeetsRequirement] = useState(false);

  useEffect(() => {
    // Load AI progress from localStorage
    const aiCompleted = JSON.parse(localStorage.getItem('sifiso-ai-completed') || '[]');
    setCompletedAILevels(aiCompleted);

    // Check Golden Learn progress for entry requirement
    const goldenLearnCompleted = JSON.parse(localStorage.getItem('sifiso-completed') || '[]');
    const progressCount = goldenLearnCompleted.filter(Boolean).length;
    setGoldenLearnProgress(progressCount);
    setMeetsRequirement(progressCount >= 10);
  }, []);

  const completedCount = completedAILevels.filter(Boolean).length;
  const progressPercentage = Math.round((completedCount / aiLevels.length) * 100);

  const handleLevelClick = (levelIndex) => {
    if (!meetsRequirement) return;

    // Level 1 is always unlocked, others require previous level completion
    if (levelIndex === 0 || completedAILevels[levelIndex - 1]) {
      onSelectLevel(levelIndex);
    }
  };

  const isLevelUnlocked = (levelIndex) => {
    if (!meetsRequirement) return false;
    return levelIndex === 0 || completedAILevels[levelIndex - 1];
  };

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: '#F5F5F0',
      color: '#2A2A2A',
      paddingBottom: '60px'
    }}>
      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '40px 20px' }}>
        {/* Header */}
        <header style={{ marginBottom: '40px' }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '30px'
          }}>
            <div>
              <h1 style={{
                fontSize: '2.5rem',
                fontWeight: '800',
                color: '#D4AF37',
                marginBottom: '10px',
                letterSpacing: '-0.5px'
              }}>
                GOLDEN AI
              </h1>
              <p style={{
                fontSize: '1.2rem',
                fontStyle: 'italic',
                color: '#6B6B6B'
              }}>
                Augmented Thinking
              </p>
            </div>
            <button
              onClick={onBackToSIFISO}
              style={{
                padding: '12px 24px',
                backgroundColor: '#2A2A2A',
                color: '#F5F5F0',
                border: 'none',
                borderRadius: '8px',
                fontSize: '0.95rem',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#3A3A3A'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#2A2A2A'}
            >
              ← Return to SIFISO
            </button>
          </div>

          <div style={{
            height: '3px',
            background: 'linear-gradient(to right, #D4AF37, transparent)',
            marginBottom: '30px'
          }} />

          {/* Progress Section */}
          {meetsRequirement ? (
            <div style={{
              backgroundColor: 'white',
              border: '2px solid #D4AF37',
              borderRadius: '12px',
              padding: '25px'
            }}>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '15px'
              }}>
                <span style={{
                  fontSize: '1.1rem',
                  fontWeight: '600',
                  color: '#2A2A2A'
                }}>
                  Your Progress
                </span>
                <span style={{
                  fontSize: '1.3rem',
                  fontWeight: '700',
                  color: '#D4AF37'
                }}>
                  {completedCount} / {aiLevels.length}
                </span>
              </div>
              <div style={{
                width: '100%',
                height: '12px',
                backgroundColor: '#E5E5D5',
                borderRadius: '6px',
                overflow: 'hidden'
              }}>
                <div style={{
                  width: `${progressPercentage}%`,
                  height: '100%',
                  backgroundColor: '#D4AF37',
                  transition: 'width 0.3s ease'
                }} />
              </div>
              <p style={{
                fontSize: '0.9rem',
                color: '#6B6B6B',
                marginTop: '10px',
                marginBottom: 0
              }}>
                {progressPercentage}% Complete
              </p>
            </div>
          ) : (
            // Entry requirement not met
            <div style={{
              backgroundColor: '#FFF3CD',
              border: '2px solid #D4AF37',
              borderRadius: '12px',
              padding: '25px'
            }}>
              <div style={{
                fontSize: '1.1rem',
                fontWeight: '700',
                color: '#856404',
                marginBottom: '15px'
              }}>
                Entry Requirement
              </div>
              <p style={{
                fontSize: '1rem',
                color: '#2A2A2A',
                lineHeight: '1.7',
                marginBottom: '15px'
              }}>
                Golden AI requires foundational systems thinking. Complete Level 10 of Golden Learn first to unlock this content.
              </p>
              <p style={{
                fontSize: '0.95rem',
                color: '#6B6B6B',
                margin: 0
              }}>
                Your Golden Learn progress: <strong>{goldenLearnProgress} / 30 levels</strong>
                <br />
                You need: <strong>10 levels minimum</strong>
              </p>
            </div>
          )}
        </header>

        {/* Levels Grid */}
        <main>
          <h2 style={{
            fontSize: '1.5rem',
            fontWeight: '700',
            color: '#2A2A2A',
            marginBottom: '25px'
          }}>
            12 Levels of AI Literacy
          </h2>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
            gap: '20px'
          }}>
            {aiLevels.map((level, index) => {
              const isCompleted = completedAILevels[index];
              const isUnlocked = isLevelUnlocked(index);
              const isLocked = !isUnlocked;

              return (
                <div
                  key={level.id}
                  onClick={() => handleLevelClick(index)}
                  style={{
                    backgroundColor: isCompleted ? '#FFFBF0' : 'white',
                    border: isCompleted ? '2px solid #D4AF37' : '1px solid #E5E5D5',
                    borderRadius: '12px',
                    padding: '25px',
                    cursor: isUnlocked ? 'pointer' : 'not-allowed',
                    opacity: isLocked ? 0.5 : 1,
                    transition: 'all 0.2s ease',
                    position: 'relative'
                  }}
                  onMouseEnter={(e) => {
                    if (isUnlocked) {
                      e.currentTarget.style.transform = 'translateY(-4px)';
                      e.currentTarget.style.boxShadow = '0 4px 12px rgba(212, 175, 55, 0.2)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                >
                  {/* Status Badge */}
                  <div style={{
                    position: 'absolute',
                    top: '15px',
                    right: '15px',
                    fontSize: '1.3rem'
                  }}>
                    {isCompleted ? '✓' : isLocked ? '🔒' : '○'}
                  </div>

                  {/* Level Number */}
                  <div style={{
                    fontSize: '0.85rem',
                    fontWeight: '700',
                    textTransform: 'uppercase',
                    letterSpacing: '1px',
                    color: '#D4AF37',
                    marginBottom: '12px'
                  }}>
                    Level {level.id}
                  </div>

                  {/* Title */}
                  <h3 style={{
                    fontSize: '1.2rem',
                    fontWeight: '700',
                    color: '#2A2A2A',
                    marginBottom: '12px',
                    lineHeight: '1.3',
                    paddingRight: '30px'
                  }}>
                    {level.title}
                  </h3>

                  {/* Description */}
                  <p style={{
                    fontSize: '0.95rem',
                    color: '#6B6B6B',
                    lineHeight: '1.6',
                    marginBottom: '15px'
                  }}>
                    {level.description}
                  </p>

                  {/* Pillar Tags */}
                  {level.pillar && (
                    <div style={{
                      display: 'flex',
                      gap: '8px',
                      flexWrap: 'wrap'
                    }}>
                      <span style={{
                        fontSize: '0.75rem',
                        fontWeight: '600',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                        color: '#D4AF37',
                        backgroundColor: '#FFFBF0',
                        padding: '4px 10px',
                        borderRadius: '4px'
                      }}>
                        {level.pillar.primary}
                      </span>
                      {level.pillar.secondary && (
                        <span style={{
                          fontSize: '0.75rem',
                          fontWeight: '600',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px',
                          color: '#6B6B6B',
                          backgroundColor: '#F5F5F0',
                          padding: '4px 10px',
                          borderRadius: '4px'
                        }}>
                          {level.pillar.secondary}
                        </span>
                      )}
                    </div>
                  )}

                  {/* Locked Message */}
                  {isLocked && index > 0 && (
                    <div style={{
                      marginTop: '15px',
                      fontSize: '0.85rem',
                      color: '#6B6B6B',
                      fontStyle: 'italic'
                    }}>
                      Complete Level {index} to unlock
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </main>

        {/* Footer Principle */}
        <footer style={{
          marginTop: '60px',
          backgroundColor: '#2A2A2A',
          color: '#F5F5F0',
          borderRadius: '10px',
          padding: '30px',
          textAlign: 'center'
        }}>
          <p style={{
            fontSize: '1.2rem',
            fontWeight: '600',
            lineHeight: '1.7',
            margin: 0
          }}>
            AI as thinking partner, not answer machine.
            <br />
            <span style={{ color: '#D4AF37', fontStyle: 'italic' }}>
              Augment capacity, don't erode it.
            </span>
          </p>
        </footer>
      </div>
    </div>
  );
}

export default GoldenAIDashboard;
